/**
 * Générateur — Calculer la valeur d'une expression littérale par substitution.
 * Attendu ID : 933 | Niveau : 5ème | Domaine : Nombres et calculs / Calcul littéral
 *
 * Plan de cartes (mise en conformité avec la spécification 933, campagne 5e,
 * 2026-09-22) :
 *
 *   Carte « Multiplications invisibles » (id invisibles) — niveaux 1 et 2
 *     invisibles_texte_trou_saisie + invisibles_qcm_unique : cinq formes
 *       (aL, aL + b, aL − b, L + b avec coefficient implicite, a + bL).
 *       Les signes de multiplication sont AFFICHÉS au niveau 1 et masqués au
 *       niveau 2 (variables.signes_de_multiplication_affiches).
 *
 *   Carte « Compléter un tableau de valeurs » (id tableau) — variante transverse
 *     tableau_texte_trou_saisie + tableau_qcm_unique : plusieurs valeurs
 *       portées dans un tableau, expressions du premier et du second degré.
 *
 *   Carte « Substituer dans une expression avec parenthèses » (id parentheses)
 *     — niveau 2
 *     parentheses_texte_trou_saisie + parentheses_qcm_unique : k(x + c) et
 *       ax(bx + c), la parenthèse calculée en premier.
 *
 *   Carte « Substituer un nombre relatif » (id relatifs) — niveau 3
 *     relatifs_texte_trou_saisie + relatifs_qcm_unique : valeur entière
 *       relative, TOUJOURS parenthésée dans la correction ; second degré admis.
 *
 *   Carte « Substituer un nombre décimal » (id decimaux) — niveau 4
 *     decimaux_texte : valeur à une ou deux décimales, et les valeurs 0 et 1
 *       réintroduites à dessein.
 *
 *   Carte « Deux ou trois lettres, une feuille » (id lettres) — niveau 5
 *     lettres_texte_trou_saisie + lettres_qcm_unique : valeurs données une fois
 *       pour toutes, deux expressions voisines mises en regard.
 *
 *   Carte « Substituer une fraction » (id fraction) — niveau 6
 *     fraction_texte : valeur substituée fractionnaire irréductible.
 *
 *   Carte « Aire du polygone en fonction de x » (id aire) — variante transverse
 *     aire_valeur_saisie + aire_texte_trou_saisie : formule donnée dans un
 *       contexte concret, figure fournie et jamais construite.
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\933.json) :
 *   Bornes appliquées : valeur entière substituée −15..15 (positive hors du
 *   niveau 3) ; valeur décimale −9,9..9,9 à une ou deux décimales ; valeur
 *   fractionnaire de dénominateur 2..12, numérateur premier avec lui ; 1 à 3
 *   lettres, tirées dans x, y, z, t, a, b, c, n ; coefficients 1..15 (la valeur
 *   1 seulement à partir du niveau 3, et comme coefficient implicite) ; 1 à 5
 *   opérations ; degré 1 ou 2.
 *   Exclusions appliquées : aucun produit de deux binômes, aucun cube ; aucune
 *   réduction préalable demandée ; la valeur substituée diffère de tous les
 *   coefficients de l'expression ; 0 et 1 écartés aux niveaux 1 et 2 et
 *   réintroduits au niveau 4 ; valeurs distinctes deux à deux quand plusieurs
 *   lettres interviennent ; aucun dénominateur contenant la lettre ; aucun
 *   résultat à arrondir (les décimaux sont calculés en centièmes entiers).
 *   Erreurs visées → mots-clés :
 *     parenthèses du négatif oubliées      → parentheses_du_negatif_oubliees
 *     juxtaposition lue comme une somme    → juxtaposition_lue_comme_somme
 *     juxtaposition lue comme un nombre    → juxtaposition_concatenee
 *     carré confondu avec le double        → carre_et_double
 *     calcul de gauche à droite            → sans_hierarchie
 *     une seule occurrence substituée      → une_seule_occurrence
 *     valeurs de deux lettres permutées    → valeurs_permutees
 *     proportionnalité supposée            → proportionnalite_supposee
 *     coefficient implicite oublié         → coefficient_implicite_oublie
 *     virgule perdue dans un produit       → virgule_perdue
 *   Graduation → cartes : 1 → invisibles_* (signes affichés) ; 2 →
 *   invisibles_* (signes masqués, coefficient implicite), parentheses_* ;
 *   3 → relatifs_* ; 4 → decimaux_texte ; 5 → lettres_* ; 6 → fraction_texte ;
 *   variante transverse (contexte, plusieurs valeurs) → tableau_*, aire_*.
 *   Non tranché : au niveau 6, le coefficient est choisi multiple du
 *   dénominateur pour que le résultat soit entier ; le `type_reponse` prévoit
 *   un clavier « augmenté de la fraction », qui n'existe pas dans le dépôt.
 *   La correction « en colonne alignée » est rendue par les lignes successives
 *   d'une phrase à trous.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 933;

const L_INVISIBLES = 'Multiplications invisibles (20x = 20 × x)';
const L_TABLEAU = 'Compléter un tableau de valeurs';
const L_PARENTHESES = 'Substituer dans une expression avec parenthèses';
const L_RELATIFS = 'Substituer un nombre relatif';
const L_DECIMAUX = 'Substituer un nombre décimal';
const L_LETTRES = 'Deux ou trois lettres, une feuille';
const L_FRACTION = 'Substituer une fraction';
const L_AIRE = 'Aire du polygone en fonction de x';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement, jamais en saisie)

// ─── Bornes de la spécification ─────────────────────────────────────────
const LETTRES = ['x', 'y', 'z', 't', 'a', 'b', 'c', 'n'];  // e, i, o exclues
const VAL_MIN = -15;
const VAL_MAX = 15;
const COEF_MIN = 1;
const COEF_MAX = 15;

/**
 * Le coefficient le plus grand qu'on accepte devant une valeur à DEUX décimales.
 *
 * « 7c + 12 pour c = 8,91 » demande de poser 7 × 891 puis de placer la virgule : un
 * exercice de multiplication posée qui se superpose à la substitution, alors que c'est
 * la substitution qu'on évalue (retour prof 2026-09-24). En QCM l'estimation suffisait
 * à départager les propositions, mais la carte de SAISIE partage le même tirage et
 * réclame la valeur exacte.
 *
 * Le cas à deux décimales reste servi : la spécification y tient explicitement, un élève
 * qui n'a jamais substitué un décimal croit que la lettre désigne un entier. Il est
 * seulement réservé aux petits coefficients — le pire tirage passe de 9 × 9,99 + 15,
 * soit 104,91, à 5 × 9,99 + 15, soit 64,95.
 */
const COEF_MAX_DEUX_DECIMALES = 5;
const DEN_FRACTION_MIN = 2;
const DEN_FRACTION_MAX = 12;
const NOMS = ['A', 'B', 'M', 'Z'];

const pgcd = (a, b) => (b === 0 ? Math.abs(a) : pgcd(b, a % b));
/** Affichage d'un entier relatif : le vrai signe moins (U+2212). */
const aff = (v) => String(v).replace('-', MOINS);

/**
 * Une lettre de variable s'écrit en italique mathématique, jamais en lettre
 * ordinaire (règle d'équipe, retour prof 2026-09-24). Trois rendus, trois écritures :
 *   - ÉNONCÉ (`payload.texte`) : la regex de rendu est STRICTE, le fragment doit
 *     commencer par une commande LaTeX → `tex()` pose le `\mathrm{}` de largeur nulle ;
 *   - PHRASE À TROUS mixte, CHOIX de QCM, étiquettes : regex PERMISSIVE → le « $x$ »
 *     nu suffit, et `\mathrm{}` y est interdit.
 * `enLatex` traduit notre écriture d'AFFICHAGE en LaTeX : sans elle, KaTeX espacerait
 * la virgule décimale et lirait mal le signe moins typographique.
 *
 * ⚠️ Dans une phrase à trous, ne jamais produire une ligne qui COMMENCE et FINIT par
 * « $ » : `hasMathLines` ferait basculer toute la phrase en mode KaTeX global.
 */
const enLatex = (s) => String(s)
    .replace(/²/g, '^2')
    .replace(/³/g, '^3')
    .replace(/−/g, '-')
    .replace(/\s*×\s*/g, ' \\times ')
    .replace(/\s*÷\s*/g, ' \\div ')
    .replace(/(\d),(\d)/g, '$1{,}$2');
const lettreEnonce = (L) => tex(L);
const lettreTrou = (L) => `$${L}$`;
/** Une expression entière : déjà en `$…$` → laissée telle quelle. */
const mathEnonce = (s) => (String(s).includes('$') ? String(s) : tex(enLatex(s)));
const mathChoix = (s) => (String(s).includes('$') ? String(s) : `$${enLatex(s)}$`);

// ═════════ Carte 1 — Multiplications invisibles (niveaux 1 et 2) ═════════

/**
 * Cinq formes ; `signes` dit si la multiplication est affichée (niveau 1) ou
 * masquée (niveau 2). La valeur substituée est toujours différente de chaque
 * coefficient de l'expression (exclusions 3) et vaut au moins 2 (exclusions 4).
 */
const tirerInvisibles = () => {
    for (let essai = 0; essai < 120; essai++) {
        const forme = randomChoice(['produit', 'plus', 'moins', 'implicite', 'constanteProduit']);
        const L = randomChoice(LETTRES);
        const nom = randomChoice(NOMS);
        const signes = forme === 'implicite' ? false : randomChoice([true, false]);
        const a = randInt(2, COEF_MAX);
        const b = randInt(2, COEF_MAX);
        const v = randInt(2, VAL_MAX);
        const ecrire = (coef) => (signes ? `${coef} × ${L}` : `${coef}${L}`);
        if (forme === 'produit') {
            if (v === a) continue;
            return {
                forme, L, nom, signes, a, v, niveau: signes ? 1 : 2, nbOperations: 1,
                expression: ecrire(a), resultat: a * v,
                etapes: [`${a} × {{${v}}}`, `{{${a * v}}}`],
                candidats: [
                    { code: 'juxtaposition_lue_comme_somme', valeur: a + v },
                    { code: 'juxtaposition_concatenee', valeur: Number(`${a}${v}`) },
                    { code: 'coefficient_implicite_oublie', valeur: a },
                ],
            };
        }
        if (forme === 'plus') {
            if (v === a || v === b) continue;
            return {
                forme, L, nom, signes, a, b, v, niveau: signes ? 1 : 2, nbOperations: 2,
                expression: `${ecrire(a)} + ${b}`, resultat: a * v + b,
                etapes: [`${a} × {{${v}}} + ${b}`, `{{${a * v}}} + ${b}`, `{{${a * v + b}}}`],
                candidats: [
                    { code: 'juxtaposition_lue_comme_somme', valeur: a + v + b },
                    { code: 'juxtaposition_concatenee', valeur: Number(`${a}${v}`) + b },
                    { code: 'sans_hierarchie', valeur: a * (v + b) },
                    { code: 'proportionnalite_supposee', valeur: v * (a + b) },
                ],
            };
        }
        if (forme === 'moins') {
            if (v === a || v === b || b >= a * v) continue;
            return {
                forme, L, nom, signes, a, b, v, niveau: signes ? 1 : 2, nbOperations: 2,
                expression: `${ecrire(a)} ${MOINS} ${b}`, resultat: a * v - b,
                etapes: [`${a} × {{${v}}} ${MOINS} ${b}`, `{{${a * v}}} ${MOINS} ${b}`, `{{${a * v - b}}}`],
                candidats: [
                    { code: 'juxtaposition_lue_comme_somme', valeur: a + v - b },
                    { code: 'sans_hierarchie', valeur: a * (v - b) },
                    { code: 'juxtaposition_concatenee', valeur: Number(`${a}${v}`) - b },
                ],
            };
        }
        if (forme === 'implicite') {
            // Coefficient implicite : L + b s'écrit sans le 1 (graduation niveau 2)
            if (v === b || v === 1) continue;
            return {
                forme, L, nom, signes: false, b, v, niveau: 2, nbOperations: 1,
                expression: `${L} + ${b}`, resultat: v + b,
                etapes: [`{{${v}}} + ${b}`, `{{${v + b}}}`],
                candidats: [
                    { code: 'coefficient_implicite_oublie', valeur: b },
                    { code: 'juxtaposition_concatenee', valeur: Number(`${v}${b}`) },
                    { code: 'sans_hierarchie', valeur: v * b },
                ],
            };
        }
        if (v === a || v === b) continue;
        return {
            forme, L, nom, signes, a, b, v, niveau: signes ? 1 : 2, nbOperations: 2,
            expression: `${a} + ${ecrire(b)}`, resultat: a + b * v,
            etapes: [`${a} + ${b} × {{${v}}}`, `${a} + {{${b * v}}}`, `{{${a + b * v}}}`],
            candidats: [
                { code: 'sans_hierarchie', valeur: (a + b) * v },
                { code: 'juxtaposition_lue_comme_somme', valeur: a + b + v },
                { code: 'juxtaposition_concatenee', valeur: a + Number(`${b}${v}`) },
            ],
        };
    }
    // Repli déterministe : A = 20 × x pour x = 5
    return {
        forme: 'produit', L: 'x', nom: 'A', signes: true, a: 12, v: 5,
        niveau: 1, nbOperations: 1, expression: '12 × x', resultat: 60,
        etapes: ['12 × {{5}}', '{{60}}'],
        candidats: [
            { code: 'juxtaposition_lue_comme_somme', valeur: 17 },
            { code: 'juxtaposition_concatenee', valeur: 125 },
            { code: 'coefficient_implicite_oublie', valeur: 12 },
        ],
    };
};

const trois = (correct, candidats) => {
    const vus = new Set([aff(correct)]);
    const retenus = [];
    shuffle(candidats).forEach(d => {
        const s = aff(d.valeur);
        if (vus.has(s) || !Number.isInteger(d.valeur)) return;
        vus.add(s);
        retenus.push({ code: d.code, valeur: s });
    });
    return retenus.length >= 3 ? retenus.slice(0, 3) : null;
};

const genInvisiblesTrous = () => {
    const t = tirerInvisibles();
    const phrase = t.etapes.map(e => `${t.nom} = ${e}`).join('\n');
    const payload = makeTexteTrou(phrase,
        `<p><b>${t.nom} = ${mathEnonce(t.expression)}. Calcule ${t.nom} pour `
        + `${lettreEnonce(t.L)} = ${t.v}.</b>`
        + (t.signes ? '</p>'
            : (t.forme === 'implicite'
                ? ' Attention : le coefficient de la lettre ne s\'écrit pas quand il vaut 1.</p>'
                : ' Écris d\'abord la multiplication cachée dans l\'écriture.</p>')),
        { ...opts(L_INVISIBLES), mode: 'saisie' });
    payload._meta.params = {
        ...t, candidats: undefined, nbVariables: 1,
        erreurVisee: t.forme === 'implicite'
            ? ['coefficient_implicite_oublie']
            : ['juxtaposition_lue_comme_somme'],
    };
    return payload;
};

const genInvisiblesQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerInvisibles();
        const distracteurs = trois(t.resultat, t.candidats);
        if (!distracteurs) continue;
        const correct = aff(t.resultat);
        const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
            `<p><b>${t.nom} = ${mathEnonce(t.expression)}. Que vaut ${t.nom} pour `
            + `${lettreEnonce(t.L)} = ${t.v} ?</b></p>`,
            opts(L_INVISIBLES));
        payload._meta.params = { ...t, candidats: undefined, nbVariables: 1, distracteurs };
        return payload;
    }
    const distracteurs = [
        { code: 'juxtaposition_lue_comme_somme', valeur: '17' },
        { code: 'juxtaposition_concatenee', valeur: '125' },
        { code: 'coefficient_implicite_oublie', valeur: '12' },
    ];
    const payload = makeQcm(['60', ...distracteurs.map(d => d.valeur)], '60',
        `<p><b>A = ${mathEnonce('12 × x')}. Que vaut A pour ${lettreEnonce('x')} = 5 ?</b></p>`,
        opts(L_INVISIBLES));
    payload._meta.params = {
        forme: 'produit', L: 'x', a: 12, v: 5, niveau: 1, nbOperations: 1,
        nbVariables: 1, distracteurs,
    };
    return payload;
};

// ═════════ Carte 2 — Tableau de valeurs (variante transverse) ═════════

const T_TH = 'background:#1E3A8A;color:#fff;padding:6px 14px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:6px 14px;border:1px solid #64748b;text-align:center';
const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';
const tableEnonce = (...lignes) =>
    `<table style="border-collapse:collapse;margin:12px auto">${lignes.join('')}</table>`;

// Degré 1 ou 2 (variables.degre_maximal_de_l_expression), aucun cube, aucun
// produit de deux binômes (exclusions 1). Le coefficient implicite 1 figure
// dans « x + 7 » et dans « x² + 1 ».
const EXPRS_TABLEAU = [
    { txt: 'x² + 1', f: (x) => x * x + 1, degre: 2, a: 1, b: 1, implicite: true },
    { txt: `x² ${MOINS} 1`, f: (x) => x * x - 1, degre: 2, a: 1, b: -1, implicite: true },
    { txt: 'x² + 3x', f: (x) => x * x + 3 * x, degre: 2, a: 1, b: 0, deuxOccurrences: 3 },
    { txt: '2x + 3', f: (x) => 2 * x + 3, degre: 1, a: 2, b: 3 },
    { txt: `5x ${MOINS} 2`, f: (x) => 5 * x - 2, degre: 1, a: 5, b: -2 },
    { txt: '4x + 2', f: (x) => 4 * x + 2, degre: 1, a: 4, b: 2 },
    { txt: `3x ${MOINS} 1`, f: (x) => 3 * x - 1, degre: 1, a: 3, b: -1 },
    { txt: 'x + 7', f: (x) => x + 7, degre: 1, a: 1, b: 7, implicite: true },
];

const genTableauTrous = () => {
    const [e1, e2] = shuffle(EXPRS_TABLEAU).slice(0, 2);
    const xs = [1, 2, 3, 4];
    // 2 cases masquées par ligne, positions distinctes tirées
    const trous1 = shuffle([0, 1, 2, 3]).slice(0, 2).sort();
    const trous2 = shuffle([0, 1, 2, 3]).slice(0, 2).sort();
    const tableau = tableEnonce(
        ligneTable(lettreEnonce('x'), xs.map(String)),
        ligneTable(mathEnonce(e1.txt), xs.map((x, i) => (trous1.includes(i) ? '…' : String(e1.f(x))))),
        ligneTable(mathEnonce(e2.txt), xs.map((x, i) => (trous2.includes(i) ? '…' : String(e2.f(x))))),
    );
    // Une case par ligne (l'impression coupait une ligne portant deux trous).
    const x = lettreTrou('x');
    const phrase = [
        ...trous1.map(i => `Pour ${x} = ${xs[i]}, ${mathChoix(e1.txt)} = {{${e1.f(xs[i])}}}`),
        ...trous2.map(i => `Pour ${x} = ${xs[i]}, ${mathChoix(e2.txt)} = {{${e2.f(xs[i])}}}`),
    ].join('\n');
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète le tableau de valeurs.</b> '
        + `Les valeurs de ${lettreEnonce('x')} valent pour toute la ligne.</p>\n${tableau}`,
        { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = {
        e1: e1.txt, e2: e2.txt, trous1, trous2, niveau: 'transverse', nbVariables: 1,
        nbOperations: 2, erreurVisee: ['proportionnalite_supposee'],
    };
    return payload;
};

const genTableauQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const e = randomChoice(EXPRS_TABLEAU);
        const x = randInt(2, 9);
        const candidats = [];
        if (e.degre === 2) candidats.push({ code: 'carre_et_double', valeur: e.f(x) - x * x + 2 * x });
        if (e.deuxOccurrences) {
            candidats.push({ code: 'une_seule_occurrence', valeur: x * x + e.deuxOccurrences });
        }
        candidats.push({ code: 'proportionnalite_supposee', valeur: x * e.f(1) });
        if (e.degre === 1) {
            candidats.push({ code: 'juxtaposition_lue_comme_somme', valeur: e.a + x + e.b });
            candidats.push({ code: 'juxtaposition_concatenee', valeur: Number(`${e.a}${x}`) + e.b });
        }
        if (e.implicite) candidats.push({ code: 'coefficient_implicite_oublie', valeur: e.b });
        const distracteurs = trois(e.f(x), candidats);
        if (!distracteurs) continue;
        const correct = aff(e.f(x));
        const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
            `<p><b>Que vaut l'expression ${mathEnonce(e.txt)} pour ${lettreEnonce('x')} = ${x} ?</b></p>`,
            opts(L_TABLEAU));
        payload._meta.params = {
            e: e.txt, x, niveau: 'transverse', nbVariables: 1,
            nbOperations: e.degre === 2 ? 3 : 2, distracteurs,
        };
        return payload;
    }
    const distracteurs = [
        { code: 'carre_et_double', valeur: '7' },
        { code: 'proportionnalite_supposee', valeur: '6' },
        { code: 'sans_hierarchie', valeur: '12' },
    ];
    const payload = makeQcm(['10', ...distracteurs.map(d => d.valeur)], '10',
        `<p><b>Que vaut l'expression ${mathEnonce('x² + 1')} pour ${lettreEnonce('x')} = 3 ?</b></p>`,
        opts(L_TABLEAU));
    payload._meta.params = {
        e: 'x² + 1', x: 3, niveau: 'transverse', nbVariables: 1, nbOperations: 2, distracteurs,
    };
    return payload;
};

// ═════════ Carte 3 — Avec parenthèses (niveau 2) ═════════

const tirerParentheses = () => {
    for (let essai = 0; essai < 120; essai++) {
        const forme = randomChoice(['simple', 'produit']);
        const nom = randomChoice(['V', 'A', 'R']);
        if (forme === 'simple') {
            const k = randInt(2, 9);
            const c = randInt(2, COEF_MAX);
            const v = randInt(2, COEF_MAX);
            if (v === k || v === c) continue;          // exclusions 3
            return {
                forme, nom, k, c, v, niveau: 2, nbOperations: 2, resultat: k * (v + c),
                expression: `${k}(x + ${c})`,
                etapes: [`${k} × ({{${v}}} + ${c})`, `${k} × {{${v + c}}}`, `{{${k * (v + c)}}}`],
                candidats: [
                    { code: 'sans_hierarchie', valeur: k * v + c },
                    { code: 'juxtaposition_lue_comme_somme', valeur: k + v + c },
                    { code: 'proportionnalite_supposee', valeur: k * v + k * c + k },
                ],
            };
        }
        const a = randInt(2, 5);
        const b = randInt(2, 4);
        const c = randInt(2, 9);
        const v = randInt(2, 3);
        if ([a, b, c].includes(v)) continue;
        return {
            forme, nom, a, b, c, v, niveau: 2, nbOperations: 4,
            resultat: a * v * (b * v + c),
            expression: `${a}x(${b}x + ${c})`,
            etapes: [
                `{{${a * v}}} × ({{${b * v}}} + ${c})`,
                `{{${a * v}}} × {{${b * v + c}}}`,
                `{{${a * v * (b * v + c)}}}`,
            ],
            candidats: [
                { code: 'sans_hierarchie', valeur: a * v * b * v + c },
                { code: 'juxtaposition_lue_comme_somme', valeur: (a + v) * (b + v + c) },
                { code: 'carre_et_double', valeur: 2 * a * v * (b * v + c) },
            ],
        };
    }
    return {
        forme: 'simple', nom: 'V', k: 3, c: 7, v: 4, niveau: 2, nbOperations: 2, resultat: 33,
        expression: '3(x + 7)',
        etapes: ['3 × ({{4}} + 7)', '3 × {{11}}', '{{33}}'],
        candidats: [
            { code: 'sans_hierarchie', valeur: 19 },
            { code: 'juxtaposition_lue_comme_somme', valeur: 14 },
            { code: 'proportionnalite_supposee', valeur: 36 },
        ],
    };
};

const genParenthesesTrous = () => {
    const t = tirerParentheses();
    const phrase = t.etapes.map(e => `${t.nom} = ${e}`).join('\n');
    const payload = makeTexteTrou(phrase,
        `<p><b>${t.nom} = ${mathEnonce(t.expression)}. Calcule ${t.nom} pour `
        + `${lettreEnonce('x')} = ${t.v}.</b> `
        + 'Calcule d\'abord ce qui est entre parenthèses.</p>',
        { ...opts(L_PARENTHESES), mode: 'saisie' });
    payload._meta.params = {
        ...t, candidats: undefined, nbVariables: 1, erreurVisee: ['sans_hierarchie'],
    };
    return payload;
};

const genParenthesesQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerParentheses();
        const distracteurs = trois(t.resultat, t.candidats);
        if (!distracteurs) continue;
        const correct = aff(t.resultat);
        const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
            `<p><b>${t.nom} = ${mathEnonce(t.expression)}. Que vaut ${t.nom} pour `
            + `${lettreEnonce('x')} = ${t.v} ?</b></p>`,
            opts(L_PARENTHESES));
        payload._meta.params = { ...t, candidats: undefined, nbVariables: 1, distracteurs };
        return payload;
    }
    const distracteurs = [
        { code: 'sans_hierarchie', valeur: '19' },
        { code: 'juxtaposition_lue_comme_somme', valeur: '14' },
        { code: 'proportionnalite_supposee', valeur: '36' },
    ];
    const payload = makeQcm(['33', ...distracteurs.map(d => d.valeur)], '33',
        `<p><b>V = ${mathEnonce('3(x + 7)')}. Que vaut V pour ${lettreEnonce('x')} = 4 ?</b></p>`,
        opts(L_PARENTHESES));
    payload._meta.params = {
        forme: 'simple', nom: 'V', k: 3, c: 7, v: 4, niveau: 2, nbOperations: 2,
        nbVariables: 1, distracteurs,
    };
    return payload;
};

// ═════════ Carte 4 — Substituer un nombre relatif (niveau 3) ═════════

const tirerRelatifs = () => {
    for (let essai = 0; essai < 120; essai++) {
        const forme = randomChoice(['affine', 'carre']);
        const L = randomChoice(LETTRES);
        const nom = randomChoice(NOMS);
        const v = randInt(VAL_MIN, -2);              // valeur relative négative
        if (forme === 'affine') {
            const a = randInt(2, 9);
            const b = randInt(COEF_MIN, COEF_MAX);
            if (Math.abs(v) === a || Math.abs(v) === b) continue;
            const resultat = a * v + b;
            return {
                forme, L, nom, a, b, v, resultat, niveau: 3, nbOperations: 2,
                expression: `${a}${L} + ${b}`,
                // Les parenthèses sont ÉCRITES dans la correction (vigilance 1) ;
                // le trou ne contient que le nombre, saisissable au clavier.
                etapes: [
                    `${a} × ({{${v}}}) + ${b}`,
                    `{{${a * v}}} + ${b}`,
                    `{{${resultat}}}`,
                ],
                candidats: [
                    { code: 'parentheses_du_negatif_oubliees', valeur: a * Math.abs(v) + b },
                    { code: 'sans_hierarchie', valeur: a * (v + b) },
                    { code: 'juxtaposition_lue_comme_somme', valeur: a + v + b },
                ],
            };
        }
        const b = randInt(COEF_MIN, COEF_MAX);
        if (Math.abs(v) === b) continue;
        const resultat = v * v + b;
        return {
            forme, L, nom, b, v, resultat, niveau: 3, nbOperations: 2,
            expression: `${L}² + ${b}`,
            etapes: [
                `({{${v}}})² + ${b}`,
                `{{${v * v}}} + ${b}`,
                `{{${resultat}}}`,
            ],
            candidats: [
                { code: 'parentheses_du_negatif_oubliees', valeur: -(v * v) + b },
                { code: 'carre_et_double', valeur: 2 * v + b },
                { code: 'coefficient_implicite_oublie', valeur: b },
            ],
        };
    }
    return {
        forme: 'affine', L: 'x', nom: 'A', a: 3, b: 5, v: -4, resultat: -7,
        niveau: 3, nbOperations: 2, expression: '3x + 5',
        etapes: ['3 × ({{-4}}) + 5', '{{-12}} + 5', '{{-7}}'],
        candidats: [
            { code: 'parentheses_du_negatif_oubliees', valeur: 17 },
            { code: 'sans_hierarchie', valeur: 3 },
            { code: 'juxtaposition_lue_comme_somme', valeur: 4 },
        ],
    };
};

const genRelatifsTrous = () => {
    const t = tirerRelatifs();
    const phrase = t.etapes.map(e => `${t.nom} = ${e}`).join('\n');
    const payload = makeTexteTrou(phrase,
        `<p><b>${t.nom} = ${mathEnonce(t.expression)}. Calcule ${t.nom} pour `
        + `${lettreEnonce(t.L)} = ${MOINS}${Math.abs(t.v)}.</b> `
        + 'Mets la valeur entre parenthèses quand tu la remplaces.</p>',
        { ...opts(L_RELATIFS), mode: 'saisie' });
    payload._meta.params = {
        ...t, candidats: undefined, nbVariables: 1,
        erreurVisee: ['parentheses_du_negatif_oubliees'],
    };
    return payload;
};

const genRelatifsQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerRelatifs();
        const distracteurs = trois(t.resultat, t.candidats);
        if (!distracteurs) continue;
        const correct = aff(t.resultat);
        const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
            `<p><b>${t.nom} = ${mathEnonce(t.expression)}. Que vaut ${t.nom} pour `
            + `${lettreEnonce(t.L)} = ${MOINS}${Math.abs(t.v)} ?</b></p>`, opts(L_RELATIFS));
        payload._meta.params = { ...t, candidats: undefined, nbVariables: 1, distracteurs };
        return payload;
    }
    const distracteurs = [
        { code: 'parentheses_du_negatif_oubliees', valeur: '17' },
        { code: 'sans_hierarchie', valeur: '3' },
        { code: 'juxtaposition_lue_comme_somme', valeur: '4' },
    ];
    const payload = makeQcm(['−7', ...distracteurs.map(d => d.valeur)], '−7',
        `<p><b>A = ${mathEnonce('3x + 5')}. Que vaut A pour ${lettreEnonce('x')} = ${MOINS}4 ?</b></p>`,
        opts(L_RELATIFS));
    payload._meta.params = {
        forme: 'affine', L: 'x', a: 3, b: 5, v: -4, resultat: -7, niveau: 3,
        nbOperations: 2, nbVariables: 1, distracteurs,
    };
    return payload;
};

// ═════════ Carte 5 — Substituer un nombre décimal (niveau 4) ═════════

/**
 * Calculs conduits sur des ENTIERS de dixièmes ou de centièmes puis virgule
 * remise (points_de_vigilance 5) : la réponse attendue est exacte.
 * Les valeurs 0 et 1, écartées des niveaux d'entrée, sont réintroduites ici
 * (exclusions 4, ecarts_assumes 3).
 */
const tirerDecimaux = () => {
    for (let essai = 0; essai < 120; essai++) {
        const L = randomChoice(LETTRES);
        const nom = randomChoice(NOMS);
        const cas = randomChoice(['decimal', 'decimal', 'decimal', 'zeroUn']);
        // Le nombre de décimales se tire AVANT le coefficient : deux décimales ne vont
        // qu'avec un petit coefficient (voir COEF_MAX_DEUX_DECIMALES).
        const decimales = cas === 'zeroUn' ? 0 : randomChoice([1, 2]);
        const a = decimales === 2 ? randInt(2, COEF_MAX_DEUX_DECIMALES) : randInt(2, 9);
        const b = randInt(2, COEF_MAX);
        if (cas === 'zeroUn') {
            const v = randomChoice([0, 1]);
            return {
                cas, L, nom, a, b, v, niveau: 4, nbOperations: 2,
                expression: `${a}${L} + ${b}`,
                resultatCent: (a * v + b) * 100,
                candidats: [
                    { code: 'coefficient_implicite_oublie', valeur: (v + b) * 100 },
                    { code: 'juxtaposition_lue_comme_somme', valeur: (a + v + b) * 100 },
                    { code: 'sans_hierarchie', valeur: a * (v + b) * 100 },
                ],
            };
        }
        const echelle = decimales === 1 ? 10 : 100;
        const entier = randInt(11, 99 * (echelle / 10));     // ≤ 9,9
        if (entier % echelle === 0) continue;                // au moins une décimale
        const vCent = entier * (100 / echelle);              // en centièmes
        if (vCent === a * 100 || vCent === b * 100) continue; // exclusions 3
        const resultatCent = a * vCent + b * 100;
        return {
            cas, L, nom, a, b, decimales, vCent, niveau: 4, nbOperations: 2,
            expression: `${a}${L} + ${b}`,
            resultatCent,
            candidats: [
                // la virgule perdue : « 7 × 6,9 » calculé comme « 7 × 69 »
                { code: 'virgule_perdue', valeur: (a * entier + b) * 100 },
                { code: 'juxtaposition_lue_comme_somme', valeur: a * 100 + vCent + b * 100 },
                { code: 'sans_hierarchie', valeur: a * (vCent + b * 100) },
            ],
        };
    }
    return {
        cas: 'decimal', L: 'x', nom: 'A', a: 4, b: 7, decimales: 1, vCent: 250, entier: 25,
        niveau: 4, nbOperations: 2, expression: '4x + 7', resultatCent: 1700,
        candidats: [
            { code: 'virgule_perdue', valeur: 10700 },
            { code: 'juxtaposition_lue_comme_somme', valeur: 1350 },
            { code: 'sans_hierarchie', valeur: 3800 },
        ],
    };
};

/** Un nombre de centièmes rendu en écriture française exacte. */
const depuisCentiemes = (c) => {
    const signe = c < 0 ? MOINS : '';
    const n = Math.abs(c);
    const ent = Math.floor(n / 100);
    const dec = n % 100;
    if (dec === 0) return `${signe}${ent}`;
    if (dec % 10 === 0) return `${signe}${ent},${dec / 10}`;
    return `${signe}${ent},${String(dec).padStart(2, '0')}`;
};
/** La même valeur, en écriture clavier (le moins ordinaire). */
const saisieCentiemes = (c) => depuisCentiemes(c).replace(MOINS, '-');

const genDecimauxSaisie = () => {
    const t = tirerDecimaux();
    const vAffiche = t.cas === 'zeroUn' ? String(t.v) : depuisCentiemes(t.vCent);
    const payload = makeSaisie(saisieCentiemes(t.resultatCent),
        `<p><b>${t.nom} = ${mathEnonce(t.expression)}. Calcule ${t.nom} pour `
        + `${lettreEnonce(t.L)} = ${vAffiche}.</b> `
        + 'Réponse exacte, avec une virgule si besoin.</p>',
        opts(L_DECIMAUX));
    payload._meta.params = {
        ...t, candidats: undefined, nbVariables: 1,
        resultat: t.resultatCent / 100,
        erreurVisee: t.cas === 'zeroUn' ? ['coefficient_implicite_oublie'] : ['virgule_perdue'],
    };
    return payload;
};

const genDecimauxQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerDecimaux();
        const vus = new Set([saisieCentiemes(t.resultatCent)]);
        const distracteurs = [];
        shuffle(t.candidats).forEach(d => {
            const s = saisieCentiemes(d.valeur);
            if (vus.has(s)) return;
            vus.add(s);
            distracteurs.push({ code: d.code, valeur: depuisCentiemes(d.valeur) });
        });
        if (distracteurs.length < 3) continue;
        const vAffiche = t.cas === 'zeroUn' ? String(t.v) : depuisCentiemes(t.vCent);
        const correct = depuisCentiemes(t.resultatCent);
        const choix = [correct, ...distracteurs.slice(0, 3).map(d => d.valeur)];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, correct,
            `<p><b>${t.nom} = ${mathEnonce(t.expression)}. Que vaut ${t.nom} pour `
            + `${lettreEnonce(t.L)} = ${vAffiche} ?</b></p>`,
            opts(L_DECIMAUX));
        payload._meta.params = {
            ...t, candidats: undefined, nbVariables: 1,
            resultat: t.resultatCent / 100, distracteurs: distracteurs.slice(0, 3),
        };
        return payload;
    }
    const distracteurs = [
        { code: 'virgule_perdue', valeur: '8' },
        { code: 'juxtaposition_lue_comme_somme', valeur: '13,5' },
        { code: 'sans_hierarchie', valeur: '38' },
    ];
    const payload = makeQcm(['17', ...distracteurs.map(d => d.valeur)], '17',
        `<p><b>A = ${mathEnonce('4x + 7')}. Que vaut A pour ${lettreEnonce('x')} = 2,5 ?</b></p>`,
        opts(L_DECIMAUX));
    payload._meta.params = {
        cas: 'decimal', L: 'x', a: 4, b: 7, vCent: 250, resultat: 17, niveau: 4,
        nbOperations: 2, nbVariables: 1, distracteurs,
    };
    return payload;
};

// ═════════ Carte 6 — Deux ou trois lettres (niveau 5) ═════════

const tirerLettres = () => {
    for (let essai = 0; essai < 120; essai++) {
        const nb = randomChoice([2, 3]);
        const lettres = shuffle(LETTRES).slice(0, nb);
        const valeurs = shuffle([2, 3, 4, 5, 6, 7, 8, 9, 10]).slice(0, nb);  // distinctes (exclusions 5)
        const k = randInt(2, 9);
        if (valeurs.includes(k)) continue;
        const [A, B, C] = lettres;
        const [va, vb, vc] = valeurs;
        // Deux expressions VOISINES mises en regard (graduation niveau 5)
        const e1 = { txt: `${k}${A} + ${B}`, val: k * va + vb };
        const e2 = { txt: `${k}(${A} + ${B})`, val: k * (va + vb) };
        const e3 = nb === 3 ? { txt: `${A} + ${B} + ${C}`, val: va + vb + vc } : null;
        if (e1.val === e2.val) continue;
        return {
            nb, lettres, valeurs, k, A, B, C, va, vb, vc, e1, e2, e3,
            niveau: 5, nbOperations: 3,
            candidats: [
                { code: 'valeurs_permutees', valeur: k * vb + va },
                { code: 'sans_hierarchie', valeur: k * (va + vb) },
                { code: 'juxtaposition_lue_comme_somme', valeur: k + va + vb },
            ],
        };
    }
    return {
        nb: 2, lettres: ['a', 'b'], valeurs: [3, 5], k: 4, A: 'a', B: 'b', C: undefined,
        va: 3, vb: 5, vc: undefined,
        e1: { txt: '4a + b', val: 17 }, e2: { txt: '4(a + b)', val: 32 }, e3: null,
        niveau: 5, nbOperations: 3,
        candidats: [
            { code: 'valeurs_permutees', valeur: 23 },
            { code: 'sans_hierarchie', valeur: 32 },
            { code: 'juxtaposition_lue_comme_somme', valeur: 12 },
        ],
    };
};

const enonceLettres = (t) =>
    '<p><b>Pour toute cette question, '
    + `${t.lettres.map((l, i) => `${lettreEnonce(l)} = ${t.valeurs[i]}`).join(', ')}.</b></p>`;

const genLettresTrous = () => {
    const t = tirerLettres();
    const lignes = [
        `${mathChoix(t.e1.txt)} = {{${t.e1.val}}}`,
        `${mathChoix(t.e2.txt)} = {{${t.e2.val}}}`,
        ...(t.e3 ? [`${mathChoix(t.e3.txt)} = {{${t.e3.val}}}`] : []),
    ];
    const payload = makeTexteTrou(lignes.join('\n'),
        enonceLettres(t) + '<p><b>Calcule la valeur de chaque expression.</b> '
        + 'Les valeurs des lettres valent pour toutes les lignes.</p>',
        { ...opts(L_LETTRES), mode: 'saisie' });
    payload._meta.params = {
        ...t, candidats: undefined, nbVariables: t.nb, erreurVisee: ['valeurs_permutees'],
    };
    return payload;
};

const genLettresQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerLettres();
        const distracteurs = trois(t.e1.val, t.candidats);
        if (!distracteurs) continue;
        const correct = aff(t.e1.val);
        const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
            enonceLettres(t) + `<p><b>Que vaut ${mathEnonce(t.e1.txt)} ?</b></p>`, opts(L_LETTRES));
        payload._meta.params = { ...t, candidats: undefined, nbVariables: t.nb, distracteurs };
        return payload;
    }
    const distracteurs = [
        { code: 'valeurs_permutees', valeur: '23' },
        { code: 'sans_hierarchie', valeur: '32' },
        { code: 'juxtaposition_lue_comme_somme', valeur: '12' },
    ];
    const payload = makeQcm(['17', ...distracteurs.map(d => d.valeur)], '17',
        `<p><b>Pour toute cette question, ${lettreEnonce('a')} = 3, ${lettreEnonce('b')} = 5.</b></p>`
        + `<p><b>Que vaut ${mathEnonce('4a + b')} ?</b></p>`,
        opts(L_LETTRES));
    payload._meta.params = {
        nb: 2, lettres: ['a', 'b'], valeurs: [3, 5], k: 4, niveau: 5, nbOperations: 3,
        nbVariables: 2, distracteurs,
    };
    return payload;
};

// ═════════ Carte 7 — Substituer une fraction (niveau 6) ═════════

/**
 * Valeur substituée p/q irréductible, q de 2 à 12. Le coefficient est un
 * multiple de q : le résultat est un entier exact (exclusions 7), et la
 * réponse reste une saisie numérique — le dépôt n'a pas de clavier à fraction.
 */
const tirerFraction = () => {
    for (let essai = 0; essai < 120; essai++) {
        const q = randInt(DEN_FRACTION_MIN, DEN_FRACTION_MAX);
        const p = randInt(1, q - 1);
        if (pgcd(p, q) !== 1) continue;
        const mMax = Math.floor(COEF_MAX / q);
        if (mMax < 1) continue;
        const m = randInt(1, mMax);
        const a = m * q;
        const b = randInt(COEF_MIN, COEF_MAX);
        const L = randomChoice(LETTRES);
        const nom = randomChoice(NOMS);
        const resultat = m * p + b;
        return {
            q, p, m, a, b, L, nom, resultat, niveau: 6, nbOperations: 3,
            expression: `${a}${L} + ${b}`,
        };
    }
    return {
        q: 3, p: 2, m: 4, a: 12, b: 5, L: 'x', nom: 'A', resultat: 13,
        niveau: 6, nbOperations: 3, expression: '12x + 5',
    };
};

const genFractionSaisie = () => {
    const t = tirerFraction();
    const payload = makeSaisie(String(t.resultat),
        `<p><b>${t.nom} = ${mathEnonce(t.expression)}. Calcule ${t.nom} pour `
        + `${lettreEnonce(t.L)} = $\\dfrac{${t.p}}{${t.q}}$.</b> Le résultat est un nombre entier.</p>`,
        opts(L_FRACTION));
    payload._meta.params = { ...t, nbVariables: 1, erreurVisee: [] };
    return payload;
};

// ═════════ Carte 8 — Un carré dans un rectangle (variante transverse) ═════════

// Carré ABCD (côté c) en haut à gauche du rectangle AEFG (largeur c + x,
// hauteur c + d). Aire BCDGFE = (c+d)(c+x) − c² = (c+d)x + cd.
const svgCarreRectangle = (c, d) => {
    const u = 22;                                  // px par cm (schématique)
    const xVis = 3;                                // largeur VISUELLE de BE (x, inconnue)
    // x0 réserve la place du libellé de gauche, qui est ancré à sa FIN en x0 − 14 :
    // « 6 cm » y mesure une trentaine de pixels, et commençait donc à −13, coupé par le
    // bord (même défaut que sur les figures du gen_932, audit du 2026-09-24).
    const x0 = 48, y0 = 26;
    const W = (c + xVis) * u, H = (c + d) * u;
    const label = (x, y, txt, anchor = 'middle') =>
        `<text x='${x}' y='${y}' text-anchor='${anchor}' font-size='13' fill='#1E3A8A' font-family='sans-serif'>${txt}</text>`;
    const sommet = (x, y, nom) =>
        `<text x='${x}' y='${y}' text-anchor='middle' font-size='13' font-weight='bold' fill='#1E3A8A' font-family='sans-serif'>${nom}</text>`;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${x0 + W + 34} ${y0 + H + 30}'>`
        // rectangle AEFG
        + `<rect x='${x0}' y='${y0}' width='${W}' height='${H}' fill='#E0E7FF' stroke='#1E3A8A' stroke-width='2'/>`
        // carré ABCD
        + `<rect x='${x0}' y='${y0}' width='${c * u}' height='${c * u}' fill='#fff' stroke='#1E3A8A' stroke-width='2'/>`
        + sommet(x0 - 9, y0 - 6, 'A') + sommet(x0 + c * u, y0 - 6, 'B') + sommet(x0 + W + 9, y0 - 6, 'E')
        + sommet(x0 + c * u + 10, y0 + c * u + 14, 'C') + sommet(x0 - 9, y0 + c * u + 4, 'D')
        + sommet(x0 - 9, y0 + H + 12, 'G') + sommet(x0 + W + 9, y0 + H + 12, 'F')
        + label(x0 + (c * u) / 2, y0 - 8, `${c} cm`)
        // La lettre en italique serif, l'unité en romain (même règle que gen_932).
        + label(x0 + c * u + (xVis * u) / 2, y0 - 8,
            '<tspan font-style=\'italic\' font-family=\'Georgia,serif\'>x</tspan> cm')
        + label(x0 - 14, y0 + c * u + (d * u) / 2 + 4, `${d} cm`, 'end')
        + '</svg>';
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

const tirerAire = () => {
    for (let essai = 0; essai < 60; essai++) {
        const c = randInt(3, 8);         // côté du carré ABCD
        const d = randInt(2, 6);         // DG : hauteur sous le carré
        const v = randInt(2, 9);
        // exclusions 3 : la valeur substituée diffère des coefficients affichés
        if (v === c + d || v === c * d || v === c || v === d) continue;
        return { c, d, v };
    }
    return { c: 5, d: 3, v: 7 };
};

const TEXTE_AIRE = (c, d) =>
    '<p><b>ABCD est un carré, AEFG est un rectangle (figure pas à l\'échelle).</b> '
    + `On donne : AB = ${c} cm ; DG = ${d} cm ; BE = ${lettreEnonce('x')} cm.</p>`;

const genAireSaisie = () => {
    const { c, d, v } = tirerAire();
    const k = c + d;                 // coefficient de x (hauteur du rectangle)
    const cst = c * d;               // terme constant
    const aire = k * v + cst;        // (c+d)v + cd : ENTIER par construction (aucun arrondi)
    const payload = makeSaisie(String(aire),
        TEXTE_AIRE(c, d)
        + '<p>L\'aire du polygone BCDGFE, en cm², est donnée par la formule '
        + `<b>A = ${mathEnonce(`${k}x + ${cst}`)}</b>. `
        + `<b>Calcule A pour ${lettreEnonce('x')} = ${v}.</b></p>`,
        { ...opts(L_AIRE), suffixe: 'cm²', elements_additionnels: svgCarreRectangle(c, d) });
    payload._meta.params = {
        c, d, v, resultat: aire, niveau: 'transverse', nbVariables: 1, nbOperations: 2,
        erreurVisee: ['proportionnalite_supposee'],
    };
    return payload;
};

const genAireTrous = () => {
    const { c, d, v } = tirerAire();
    const A1 = (c + d) * (c + v);
    const A2 = c * c;
    // (c + x) est commutatif → on pré-remplit c et on laisse x = v en trou
    // (sinon « 3 + 6 » ↔ « 6 + 3 » serait compté faux à tort)
    const phrase = `Aire de AEFG : ${c + d} × (${c} + {{${v}}}) = {{${A1}}} cm²\n`
        + `Aire de ABCD : {{${A2}}} cm²\n`
        + `Aire de BCDGFE : {{${A1}}} ${MOINS} {{${A2}}} = {{${A1 - A2}}} cm²`;
    const payload = makeTexteTrou(phrase,
        TEXTE_AIRE(c, d) + `<p>Calcule l'aire du polygone BCDGFE pour ${lettreEnonce('x')} = ${v}.</p>`,
        { ...opts(L_AIRE), mode: 'saisie', elements_additionnels: svgCarreRectangle(c, d) });
    payload._meta.params = {
        c, d, v, resultat: A1 - A2, niveau: 'transverse', nbVariables: 1, nbOperations: 4,
        erreurVisee: ['sans_hierarchie'],
    };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    invisibles_texte_trou_saisie: genInvisiblesTrous,
    invisibles_qcm_unique: genInvisiblesQcm,
    tableau_texte_trou_saisie: genTableauTrous,
    tableau_qcm_unique: genTableauQcm,
    parentheses_texte_trou_saisie: genParenthesesTrous,
    parentheses_qcm_unique: genParenthesesQcm,
    relatifs_texte_trou_saisie: genRelatifsTrous,
    relatifs_qcm_unique: genRelatifsQcm,
    decimaux_valeur_saisie: genDecimauxSaisie,
    decimaux_qcm_unique: genDecimauxQcm,
    lettres_texte_trou_saisie: genLettresTrous,
    lettres_qcm_unique: genLettresQcm,
    fraction_valeur_saisie: genFractionSaisie,
    aire_valeur_saisie: genAireSaisie,
    aire_texte_trou_saisie: genAireTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous', valeur_saisie: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'invisibles', label: L_INVISIBLES, formatsList: buildFormats('invisibles') },
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
    { id: 'parentheses', label: L_PARENTHESES, formatsList: buildFormats('parentheses') },
    { id: 'relatifs', label: L_RELATIFS, formatsList: buildFormats('relatifs') },
    { id: 'decimaux', label: L_DECIMAUX, formatsList: buildFormats('decimaux') },
    { id: 'lettres', label: L_LETTRES, formatsList: buildFormats('lettres') },
    { id: 'fraction', label: L_FRACTION, formatsList: buildFormats('fraction') },
    { id: 'aire', label: L_AIRE, formatsList: buildFormats('aire') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
